/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import {
  AuthenticationErrorMessage,
  DateTimeFormat,
  ErrorMessage,
  IErrorMessage,
  NestedErrorMessage,
  ILocalisation,
  IClinicianBrand,
} from "@advapacs/common";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IOrganisationSettings } from "../../../types/login";

export type IBrand = IClinicianBrand;

export enum LoginFormType {
  WEBAUTHN = "WEBAUTHN",
  OTP = "OTP",
  TWOFA = "TWOFA",
  DEFAULT = "DEFAULT",
}

export interface LoginAccount {
  id: string;
  email: string;
  locale: string;
  name: string;
  permissions: Array<string>;
  username: string;
  owner: boolean;
  enforceTwoFactor: boolean;
  localisation: ILocalisation;
  forcePasswordChange: boolean;
  trialMode: boolean;
  sessionId: string;
  customerId: string;
  tenantId: string;
  defaultRegion: string;
  readOnly: boolean;
  language?: string;
  showOnboarding?: boolean;
  hasAccessGrant?: boolean;
  lastLoginDateTime?: string;
  provider?: string;
  advaViewOnly?: boolean;
  showIntroduceAdvaView?: boolean;
}

export interface IClinicianPortal {
  id?: string;
  status?: string;
  logoUrl?: string;
  name: string;
  description?: string;
}

export interface IFeature {
  eUnity?: boolean;
  medDream?: boolean;
  viewer?: boolean;
  permissions?: string[];
  clinicianPortal?: IClinicianPortal;
}

interface AuthenticationState {
  sessionHasBeenFetched: boolean;
  loading: boolean;
  isAuthenticated: boolean;
  loginSuccess: boolean;
  account: LoginAccount | null;
  errorMessage: AuthenticationErrorMessage | null; // Errors returned from server side
  twoFaErrorMessage?: ErrorMessage | null; // Errors returned from server side
  passwordResetErrorMessage?: IErrorMessage | NestedErrorMessage | null; // Errors returned from server side
  pathname: string;
  expireSession: boolean;
  permissions: Array<string>;
  organisationSettings: IOrganisationSettings | null;
  loadingSettings: boolean;
  csrfToken: string;
  twoFaStatus: boolean;
  ipRestricted: boolean;
  passwordResetComplete: boolean;
  refreshing: boolean;
  webAuthnChallenge?: string;
  twoFaTypeAuthenticate?: string;
  loginFormType?: LoginFormType;
  samlErrorMessage?: ErrorMessage | null;
  organisationSettingsErrorMessage?: ErrorMessage | null;
  showVerifyOrganisation: boolean;
  features: IFeature[];
  hasForgottenOrganisation?: boolean;
  forgottenOrganisationErrorMessage?: ErrorMessage | null;
}

const initialState: AuthenticationState = {
  sessionHasBeenFetched: false,
  loading: false,
  isAuthenticated: false,
  loginSuccess: false,
  account: null,
  errorMessage: null, // Errors returned from server side
  passwordResetErrorMessage: null, // Errors returned from server side
  pathname: localStorage.getItem("path") || "/",
  expireSession: false,
  permissions: [],
  organisationSettings: null,
  loadingSettings: false,
  twoFaStatus: false,
  ipRestricted: false,
  passwordResetComplete: false,
  refreshing: false,
  csrfToken: "",
  loginFormType: LoginFormType.DEFAULT,
  organisationSettingsErrorMessage: null,
  showVerifyOrganisation: false,
  features: [] as IFeature[],
};

const PUBLIC_PATHS = [
  "/Auth/EmailValidation",
  "/ResetPassword",
  "/ForgotPassword",
  "/SSO/SAML2",
  "/Login",
  "/Studies/Share",
];

const loginSlice = createSlice({
  name: "loginSlice",
  initialState,
  reducers: {
    getOrganisationSettingsStart(state) {
      state.loadingSettings = true;
      state.organisationSettings = null;
    },
    getOrganisationSettingsSuccess(
      state,
      action: PayloadAction<IOrganisationSettings>
    ) {
      if (action.payload.samlIdps) {
        action.payload.samlIdps.sort((s1, s2) =>
          s1.name.localeCompare(s2.name)
        );
      }
      state.loadingSettings = false;
      state.organisationSettings = action.payload;
    },
    getOrganisationSettingsFail(state, { payload }) {
      state.loadingSettings = false;
      state.organisationSettings = null;
      state.organisationSettingsErrorMessage = payload;
    },
    failFetchSession(state) {
      state.sessionHasBeenFetched = true;
      state.errorMessage = null;
      state.loading = false;
      state.isAuthenticated = false;
      state.account = null;
    },
    loginStart(state) {
      state.loading = true;
      state.errorMessage = null;
      state.twoFaErrorMessage = null;
      state.samlErrorMessage = null;
    },
    startFetchSession(state) {
      state.loading = true;
      state.sessionHasBeenFetched = false;
    },
    // this function not only use for the submit login, that's why it should check the region from local storage.
    // For the issue we need to remove this region key from axios-interceptor when user expired token.
    loginSuccess(state, action: PayloadAction<LoginAccount>) {
      if (action.payload.defaultRegion && !localStorage.getItem("region")) {
        localStorage.setItem("region", action.payload.defaultRegion);
      }
      localStorage.setItem("logged", "true");
      const noViewed =
        localStorage.getItem(action.payload?.id + ".viewed") !== "true";
      const hasAccessGrant = action.payload?.hasAccessGrant;
      state.errorMessage = null;
      state.loading = false;
      state.sessionHasBeenFetched = true;
      state.isAuthenticated = true;
      state.account = action.payload;
      state.expireSession = false;
      state.permissions = state.account.permissions;
      // handle redirect after login logic for CP.
      if (noViewed && !hasAccessGrant) {
        state.pathname = "/PACSDirectory";
      }
    },
    stopLoading(state) {
      state.loading = false;
    },
    loginFail(
      state,
      action: PayloadAction<{
        err: AuthenticationErrorMessage;
        pathname: string;
      }>
    ) {
      const { err, pathname } = action.payload;
      const isPublicRoutes = PUBLIC_PATHS.includes(pathname);
      state.pathname = isPublicRoutes ? "/" : pathname;
      state.errorMessage = err;
      state.loading = false;
      state.isAuthenticated = false;
      state.account = null;
      state.sessionHasBeenFetched = true;
      // 1010 Expected MFA.
      if (err?.code === "1010") {
        if (err?.totp && err?.challenge) {
          state.loginFormType = LoginFormType.TWOFA;
        } else if (err.totp) {
          state.loginFormType = LoginFormType.OTP;
        } else if (err?.challenge) {
          state.loginFormType = LoginFormType.WEBAUTHN;
        }
      } else {
        state.loginFormType = LoginFormType.DEFAULT;
      }
    },
    webAuthCancelled(state) {
      state.loading = false;
    },
    clearAuthentication(
      state,
      action: PayloadAction<{ isReloadBrowser: boolean }>
    ) {
      state.errorMessage = null;
      state.loading = false;
      state.isAuthenticated = false;
      state.account = null;
      state.sessionHasBeenFetched = false;
      localStorage.setItem("logged", "false");

      if (action.payload.isReloadBrowser) {
        window.location.href = "/";
      }
    },
    resetPath(state) {
      state.pathname = "/";
    },
    clearSession(state, action: PayloadAction<string>) {
      state.errorMessage = null;
      state.loading = false;
      state.isAuthenticated = false;
      state.expireSession = true;
      state.sessionHasBeenFetched = false;
      if (action.payload !== "/Locked") state.pathname = action.payload;
    },
    registerTwoFaStart(state) {
      state.loading = true;
      // cancelled web auth case
      // state.twoFaErrorMessage = null;
      // state.errorMessage = null;
    },
    registerTwoFaFail(state, action: PayloadAction<ErrorMessage>) {
      state.loading = false;
      state.twoFaErrorMessage = action.payload;
    },
    getTwoFaStatusStart(state) {
      state.loading = true;
    },
    getTwoFaStatusSuccess(state, action: PayloadAction<boolean>) {
      state.twoFaStatus = action.payload;
      state.loading = false;
    },
    getTwoFaStatusFail(state, action: PayloadAction<ErrorMessage>) {
      state.twoFaErrorMessage = action.payload;
      state.loading = false;
      state.twoFaStatus = false;
    },
    getCsrfTokenStart(state) {
      state.loading = true;
    },
    getCsrfTokenSuccess(state, action: PayloadAction<{ token: string }>) {
      const { token } = action.payload;
      state.csrfToken = token || "";
      state.loading = false;
    },
    getCsrfTokenFail(state, action: PayloadAction<ErrorMessage>) {
      state.errorMessage = action.payload;
      state.loading = false;
      state.csrfToken = "";
    },
    requestPasswordResetStart(state) {
      state.loading = true;
      state.passwordResetComplete = false;
      state.passwordResetErrorMessage = null;
    },
    requestPasswordResetSuccess(state) {
      state.loading = false;
      state.passwordResetComplete = true;
      state.passwordResetErrorMessage = null;
      state.pathname = "/";
    },
    requestPasswordResetFail(state, action: PayloadAction<NestedErrorMessage>) {
      state.loading = false;
      state.passwordResetComplete = false;
      state.passwordResetErrorMessage = action.payload;
    },
    forgotOrganisationStart(state) {
      state.loading = true;
      state.hasForgottenOrganisation = false;
      state.forgottenOrganisationErrorMessage = null;
    },
    forgotOrganisationSuccess(state) {
      state.loading = false;
      state.hasForgottenOrganisation = true;
      state.forgottenOrganisationErrorMessage = null;
    },
    forgotOrganisationFail(state, action: PayloadAction<NestedErrorMessage>) {
      state.loading = false;
      state.hasForgottenOrganisation = false;
      state.forgottenOrganisationErrorMessage = action.payload;
    },
    verifyTokenStart(state) {
      state.loading = true;
      state.passwordResetErrorMessage = null;
    },
    verifyTokenSuccess(state) {
      state.loading = false;
      state.passwordResetErrorMessage = null;
    },
    verifyTokenFail(state, action: PayloadAction<NestedErrorMessage>) {
      state.loading = false;
      state.passwordResetComplete = true;
      state.passwordResetErrorMessage = action.payload;
    },
    clearPasswordReset(state) {
      state.passwordResetComplete = false;
      state.passwordResetErrorMessage = null;
    },
    restrictIp(state) {
      state.ipRestricted = true;
      state.loading = false;
    },
    setRefreshing(state, action: PayloadAction<boolean>) {
      state.refreshing = action.payload;
    },
    setEnforceTwoFactor(state, action: PayloadAction<boolean>) {
      if (state.account) {
        state.account.enforceTwoFactor = action.payload;
      }
    },
    setShowIntroduceAdvaView(state, action: PayloadAction<boolean>) {
      if (state.account) {
        state.account.showIntroduceAdvaView = action.payload;
      }
    },
    updateDateTimeFormat(state, action: PayloadAction<DateTimeFormat>) {
      const { payload } = action;
      if (state.account?.localisation) {
        if (payload?.dateFormat) {
          state.account = {
            ...state.account,
            localisation: {
              ...state.account?.localisation,
              dateFormat: payload.dateFormat,
            },
          };
        }
        if (payload?.timeFormat) {
          state.account = {
            ...state.account,
            localisation: {
              ...state.account?.localisation,
              timeFormat: payload.timeFormat,
            },
          };
        }
        if (payload?.dateTimeFormat) {
          state.account = {
            ...state.account,
            localisation: {
              ...state.account?.localisation,
              dateTimeFormat: payload.dateTimeFormat,
            },
          };
        }
      }
    },
    setTwoFaTypeAuthenticate(state, { payload }) {
      state.loginFormType = payload;
      state.twoFaErrorMessage = null;
    },
    setSAMLErrorMessage(state, action: PayloadAction<ErrorMessage>) {
      state.samlErrorMessage = action.payload;
    },
    setErrorMessageWithoutLoading(state, action) {
      state.errorMessage = action.payload;
    },
    setErrorMessage(state, { payload }) {
      state.loading = true;
      state.errorMessage = payload;
    },
    setShowVerifyOrganisation(state, { payload }) {
      state.showVerifyOrganisation = payload;
    },
    getFeaturesStart(state) {
      state.loading = true;
    },
    getFeaturesSuccess(state, action) {
      state.features = action.payload;
    },
    getFeaturesFail(state, action) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    clearLoginState(state) {
      state.loadingSettings = false;
      state.organisationSettings = null;
      state.showVerifyOrganisation = false;
      state.samlErrorMessage = null;
      state.errorMessage = null;
      state.organisationSettingsErrorMessage = null;
    },
  },
});

export const LoginAction = loginSlice.actions;
export default loginSlice.reducer;
